import { useCallback, useEffect } from "react";
import { message } from "antd";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";

export interface UseDictationOptions {
    /**
     * Called with the live transcript while recording, so the consumer can
     * mirror it into its own input/composer state. Only fires while listening.
     */
    onTranscript?: (transcript: string) => void;
    /** Recognition language. Defaults to "en-US". */
    language?: string;
}

export interface Dictation {
    /** Whether the browser supports speech recognition at all — gate the mic button on this. */
    supported: boolean;
    /** Whether we're currently recording. */
    listening: boolean;
    /** The live transcript. */
    transcript: string;
    /** Clear the transcript. */
    resetTranscript: () => void;
    /** Toggle recording. On start it actively requests mic permission (see below). */
    toggle: () => void;
    /** Stop recording (no-op if not listening). */
    stop: () => void;
}

/**
 * Shared microphone/dictation hook wrapping `react-speech-recognition`.
 *
 * Why this exists instead of using the library directly: the library latches
 * `isMicrophoneAvailable` to `false` on a single `not-allowed` error and never
 * clears it, so any code that gates the mic button on that flag stays broken
 * until a full page reload. This hook instead actively calls `getUserMedia` on
 * each start to (re)trigger the browser permission prompt — so dictation
 * self-heals the moment the user grants access, no reload needed — surfaces a
 * real error toast on denial, and aborts recognition on unmount.
 */
export function useDictation(options: UseDictationOptions = {}): Dictation {
    const { onTranscript, language = "en-US" } = options;
    const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } =
        useSpeechRecognition();

    // Mirror the live transcript into the consumer's input while recording.
    useEffect(() => {
        if (listening) onTranscript?.(transcript);
    }, [transcript, listening, onTranscript]);

    // Abort any in-flight recognition when the consumer unmounts (e.g. the
    // drawer/modal closes mid-recording).
    useEffect(() => {
        return () => {
            void SpeechRecognition.abortListening();
        };
    }, []);

    const stop = useCallback(() => {
        if (listening) void SpeechRecognition.stopListening();
    }, [listening]);

    const toggle = useCallback(() => {
        void (async () => {
            if (!browserSupportsSpeechRecognition) {
                message.error("This browser doesn't support speech recognition");
                return;
            }
            if (listening) {
                void SpeechRecognition.stopListening();
                return;
            }
            // `getUserMedia` is absent in non-secure contexts (e.g. the app
            // served over plain HTTP via a LAN IP) — speech recognition can't
            // work there either.
            if (!navigator.mediaDevices?.getUserMedia) {
                message.error(
                    "Microphone is not available in this context (requires HTTPS or localhost)."
                );
                return;
            }
            // Actively (re)request mic permission, bypassing the library's
            // sticky `isMicrophoneAvailable` flag. We only need the grant, so
            // release the device immediately and let the Web Speech API claim it.
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                stream.getTracks().forEach((track) => track.stop());
            } catch (err) {
                console.error("Microphone getUserMedia failed:", err);
                message.error(
                    "Microphone is not available. Check your browser and system microphone permissions."
                );
                return;
            }
            resetTranscript();
            void SpeechRecognition.startListening({ continuous: true, language });
        })();
    }, [browserSupportsSpeechRecognition, listening, resetTranscript, language]);

    return {
        supported: browserSupportsSpeechRecognition,
        listening,
        transcript,
        resetTranscript,
        toggle,
        stop,
    };
}
